import React, { useEffect, useRef, useState, useMemo, useCallback } from "react";
import ScrollToTopBtn from "../../../components/common/ScrollToTopBtn";
import { useAppDispatch, useAppSelector } from "../../../store/hooks";
import type { AppDispatch } from "../../../store/store";
import { useProfile } from "../../../contexts/ProfileContext";
import Loader from "../../../components/common/Loader";
import BreadCrumbs from "../../../components/common/BreadCrumbs/BreadCrumbs";
import Table, {
  TableColumn,
  TableRow,
} from "../../../components/common/Table/Table";
import CardTable from "../../../components/common/CardTable";
import { useNavigate } from "react-router-dom";
import Pagination from "../../../components/common/Table/Pagination";
import { fetchMyTeamsUBTList, setCurrentPage, fetchMyTeamsCount, deleteMyTeam } from "../../../store/slices/myTeamsSlice";
import Dropdown, { DropdownOption } from "../../../components/common/Dropdown";
import Modal from "../../../components/common/Modal";
import PopUp from "../../../components/common/PopUp/PopUp";
import {
  downloadDisplaySummaryAndDetailsReport,
  isPdfDownloadLoading,
  exportUBTDetailsByTeamsCSV,
  selectTeamsReportingExportUBTDetailsLoading,
} from "../../../store/slices/teamsReportSlice";
import {
  exportTestOfChangeCSV,
  selectReportLoading,
  selectReportError,
  clearReportState,
  downloadPipDetailsReport,
} from "../../../store/slices/reportSlice";
import {
  downloadTestSeriesReport,
  downloadMembersRolesReport,
  downloadPipHistoryReport,
  exportGLDataByTeamIds,
} from "../../../store/slices/teamReportSlice";
import { teamStorage } from "../../../utils/teamStorage";
import Notifications from "../../../components/common/Notifications";

interface ResponseForm {
  id: string;
  title: string;
  subject: string;
  message: string;
}

const MODAL_STYLES = `
  #modal-manual-trig .modal-container {
    max-width: 45% !important;
    width: 90% !important;
    top: 20% !important;
  }
`;

const helperTextContent = `
  <p><strong>Click column header to sort.</strong></p>
`;

const columns: TableColumn[] = [
  { key: "deleteIcon", label: "" },
  { key: "id", label: "ID", isSortable: true },
  { key: "ubtName", label: "Name of UBT", className: "md:max-w-[7rem] whitespace-normal break-words", isSortable: true },
  { key: "teamRating", label: "Team rating", className: "md:max-w-[9rem]" },
  {
    key: "assesmentDate",
    label: "Last face-to-face assessment",
    className: "md:max-w-[8rem]",
  },
  { key: "entity", label: "Entity" },
  { key: "area", label: "Area" },
  { key: "location", label: "Location" },
  { key: "costCenter", label: "Cost center", className: "md:max-w-[8rem]" },
  { key: "shift", label: "Shift", className: "md:pr-18!" },
  { key: "selectAction", label: "View Details" },
];

const viewDetailsOptions = [
  { id: 0, label: "Select", value: "Select" },
  { id: 2, label: "Projects", value: "projects" },
  { id: 3, label: "Members", value: "members" },
  { id: 4, label: "History", value: "history" },
  { id: 5, label: "P2P", value: "path-to-performance" },
];

import {
  TEAM_SUMMARY_OPTION_ID,
  TEAM_SUMMARY_OPTION_VALUE,
  TEAM_DETAILS_OPTION_ID,
  TEAM_DETAILS_OPTION_VALUE,
  EXPORT_TEST_DATA_OPTION_ID,
  EXPORT_TEST_DATA_OPTION_VALUE,
  EXPORT_UBT_DETAILS_OPTION_ID,
  EXPORT_UBT_DETAILS_OPTION_VALUE,
  EXPORT_TEST_OF_CHANGE_ID,
  EXPORT_TEST_OF_CHANGE_VALUE,
  EXPORT_GL_DATA_OPTION_ID,
  EXPORT_GL_DATA_OPTION_VALUE,
} from "../../../utils/reportConstants";
import {
  getMyTeamsOptions,
  EXPORT_ROLES_OPTION_ID,
  EXPORT_PIP_HISTORY_OPTION_ID,
  EXPORT_PIP_DETAILS_OPTION_ID,
} from "../../../constants/reportDropdownOptions";

/** Payload passed to fetchMyTeamsUBTList. */
interface FetchMyTeamsPayload {
  page: number;
  perPage: number;
  sortColumn: string;
  sortOrder: "ASC" | "DESC";
  countOnly: boolean;
}

/** Arguments forwarded to dispatchMyTeamsReport. */
interface ReportDispatchArgs {
  nuid: string;
  sortColumn: string;
  sortDirection: string;
  dispatch: AppDispatch;
}

/**
 * Dispatches the appropriate Redux action for the selected My Teams report type.
 * Extracted to a module-level function to keep handleReportSelect's cognitive
 * complexity within the allowed threshold.
 */
const dispatchMyTeamsReport = (
  selectedReport: DropdownOption,
  { nuid, sortColumn, sortDirection, dispatch }: ReportDispatchArgs,
): void => {
  const order = sortDirection.toUpperCase() as "ASC" | "DESC";

  if (selectedReport.id === EXPORT_GL_DATA_OPTION_ID && selectedReport.value === EXPORT_GL_DATA_OPTION_VALUE) {
    dispatch(exportGLDataByTeamIds({ nuid }));
    return;
  }
  if (selectedReport.id === EXPORT_ROLES_OPTION_ID) {
    dispatch(downloadMembersRolesReport({ nuid, sortColumn: "TEAMID", sortOrder: order }));
    return;
  }
  if (selectedReport.id === EXPORT_PIP_HISTORY_OPTION_ID) {
    dispatch(downloadPipHistoryReport({ nuid, pageId: "myTeams", sortColumn: "TEAMID", sortOrder: order }));
    return;
  }
  if (selectedReport.id === EXPORT_PIP_DETAILS_OPTION_ID) {
    dispatch(downloadPipDetailsReport({ nuid, pageId: "myTeams", sortColumn: "TEAMID", sortOrder: order }));
    return;
  }
  if (selectedReport.id === TEAM_SUMMARY_OPTION_ID && selectedReport.value === TEAM_SUMMARY_OPTION_VALUE) {
    dispatch(downloadDisplaySummaryAndDetailsReport({ reportIdentifier: "teamsummary", pageId: "ubtReportIdMyTeams", sortColumn: "teamid", sortOrder: order }));
    return;
  }
  if (selectedReport.id === TEAM_DETAILS_OPTION_ID && selectedReport.value === TEAM_DETAILS_OPTION_VALUE) {
    dispatch(downloadDisplaySummaryAndDetailsReport({ reportIdentifier: "teamdetails", pageId: "ubtReportIdMyTeams", sortColumn: "teamid", sortOrder: order }));
    return;
  }
  if (selectedReport.id === EXPORT_TEST_OF_CHANGE_ID && selectedReport.value === EXPORT_TEST_OF_CHANGE_VALUE) {
    dispatch(exportTestOfChangeCSV({ reportIdentifier: "testofchange", pageId: "ubtReportIdMyTeams", sortColumn: "TEAMID", sortOrder: order }));
    return;
  }
  if (selectedReport.id === EXPORT_TEST_DATA_OPTION_ID && selectedReport.value === EXPORT_TEST_DATA_OPTION_VALUE) {
    dispatch(downloadTestSeriesReport({ pageId: "myTeams", nuid, sortColumn: "TEAMID", sortOrder: order }));
    return;
  }
  if (selectedReport.id === EXPORT_UBT_DETAILS_OPTION_ID && selectedReport.value === EXPORT_UBT_DETAILS_OPTION_VALUE) {
    dispatch(exportUBTDetailsByTeamsCSV({ sortColumn, sortOrder: order }));
  }
};

/** Inline style for wrapping UBT name text in table cells. */
const UBT_NAME_CELL_STYLE: React.CSSProperties = { overflowWrap: "anywhere" };

const isDownloadableTeamId = (teamId: unknown): teamId is number | string =>
  (typeof teamId === "number" || typeof teamId === "string") && String(teamId).trim() !== "";

/**
 * Renders the paginated My Teams view for the current user.
 * @returns The My Teams page.
 */
const MyTeams: React.FC = () => {
  const dispatch = useAppDispatch();
  const { loading: profileLoading, profile } = useProfile();
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [showDeleteBanner, setShowDeleteBanner] = useState<boolean>(false);
  const [deletedResponseName, setDeletedResponseName] = useState<string>("");
  const [currentTeamId, setCurrentTeamId] = useState<number | null>(null);
  const [responseForms, setResponseForms] = useState<ResponseForm[]>([
    { id: '1', title: '', subject: '', message: '' }
  ]);
  const { myTeams, currentPage, perPage, loading, loadingList = loading, error, totalItems, totalCount } = useAppSelector(
    (state) => state.myTeamsSlice,
  );
  const { loading: reportLoading } = useAppSelector((state) => state.teamReportSlice);
  const pdfDownloadLoading = useAppSelector(isPdfDownloadLoading);
  const exportUBTDetailsLoading = useAppSelector(selectTeamsReportingExportUBTDetailsLoading);
  const tocExportLoading = useAppSelector(selectReportLoading);
  const tocExportError = useAppSelector(selectReportError);
  const navigate = useNavigate();

  const [sortColumn, setSortColumn] = useState<string>("TEAMID");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");
  const [uiSortColumn, setUiSortColumn] = useState<string>("id"); // Track UI column for Table component
  const notificationRef = useRef<HTMLDivElement>(null);

  // Scroll to the success notification whenever it becomes visible
  useEffect(() => {
    if (showDeleteBanner && notificationRef.current) {
      notificationRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [showDeleteBanner]);
  
  const [showTocExportErrorBanner, setShowTocExportErrorBanner] = useState<boolean>(false);


  // Show error banner when the test-of-change export fails; auto-dismiss after 5 s
  useEffect(() => {
    if (tocExportError) {
      setShowTocExportErrorBanner(true);
      const timer = setTimeout(() => {
        setShowTocExportErrorBanner(false);
        dispatch(clearReportState());
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [tocExportError, dispatch]);

  useEffect(() => {
    return () => {
      dispatch(setCurrentPage(1));
    };
  }, [dispatch]);

  // Fetch count on initial load
  useEffect(() => {
    dispatch(fetchMyTeamsCount());
  }, [dispatch]);

  useEffect(() => {
    const payload: FetchMyTeamsPayload = {
      page: currentPage,
      perPage,
      sortColumn: sortColumn ?? "TEAMID",
      sortOrder: sortDirection.toUpperCase() as "ASC" | "DESC",
      countOnly: false,
    };
    dispatch(fetchMyTeamsUBTList(payload));
  }, [currentPage, perPage, dispatch, sortColumn, sortDirection]);

  const handleViewDetailsClick = useCallback(
    (selectedOption: DropdownOption, rowData: Record<string, unknown>) => {
      const id = (rowData?.id as string | number | undefined) ?? "";
      if (id && selectedOption.value !== viewDetailsOptions[0].value) {
        teamStorage.setTeamId(id);
        if (selectedOption.value === "path-to-performance") {
          navigate('/home/path-to-performance', { 
            state: { 
              from: 'My Teams', 
              teamId: id,
              nuid: (rowData?.nuid as string | null) ?? null 
            } 
          });
        } else {
          navigate(
            `/home/${selectedOption.value}`,
            {
              state: {
                from: "My Teams",
                teamId: id,
                nuid: (rowData?.nuid as string | null) ?? null,
              },
            },
          );
        }
      }
    },
    [navigate],
  );

  const handleActionClick = useCallback(
    (rowData: Record<string, unknown>) => {
      const id = (rowData?.id as string | number | undefined) ?? "";
      if (id) {
        teamStorage.setTeamId(id);
        navigate('/home/projects', {
          state: { teamId: id, nuid: (rowData?.nuid as string | null) ?? null },
        });
      }
    },
    [navigate],
  );

  const handleBreadcrumbPrevious = useCallback((): void => {
    navigate("/home");
  }, [navigate]);

  const handlePageChange = useCallback((page: number) => {
    // Dispatch action to update currentPage in Redux, which will trigger the API call
    dispatch(setCurrentPage(page));
  }, [dispatch]);

  const getDeleteButton = (team: Record<string, unknown>) => (
    <button
      type="button"
      onClick={() => handleDeleteClick(team)}
      className="bg-transparent border-0 p-0 cursor-pointer"
      aria-label="Delete"
    >
      <img
        src="/assets/images/minus-circle-outline.svg"
        alt="Delete icon"
        className="min-w-6 min-h-6"
        width={24}
        height={24}
        style={{ display: 'block' }}
      />
    </button>
  );

  const getViewDetailsDropdown = (team: Record<string, unknown>) => (
    <Dropdown
      label=""
      selectedValue={viewDetailsOptions[0]}
      options={viewDetailsOptions}
      onSelect={(selectedValue) => handleViewDetailsClick(selectedValue, team)}
      buttonClassName="py-0! min-w-36 px-4!"
      menuClassName="min-w-36"
    />
  );

  const rows: TableRow[] = useMemo(
    () =>
      (myTeams ?? []).flatMap((team) => {
        const ubtNameCell = (
          <span className="whitespace-normal break-words" style={UBT_NAME_CELL_STYLE}>
            {team.ubtName ?? ""}
          </span>
        );

        if (!team.details || team.details.length === 0) {
          return [
            {
              rowKey: `team-${team.id}-0`,
              deleteIcon: team.isDelete && getDeleteButton(team),
              id: team.id,
              ubtName: ubtNameCell,
              teamRating: "",
              assesmentDate: "",
              entity: "",
              area: "",
              location: "",
              costCenter: "",
              shift: "",
              selectAction: getViewDetailsDropdown(team),
            },
          ];
        }

        return team.details.map((detail: Record<string, unknown>, index: number) => ({
          rowKey: `team-${team.id}-${index}`,
          deleteIcon: team.isDelete && index === 0 ? getDeleteButton(team) : null,
          id: index === 0 ? team.id : "",
          ubtName: index === 0 ? ubtNameCell : "",
          teamRating: detail.teamRating ?? "",
          assesmentDate: detail.assesmentDate ?? "",
          entity: detail.entity ?? "",
          area: detail.area ?? "",
          location: detail.location ?? "",
          costCenter: detail.costCenter ?? "",
          shift: detail.shift ?? "",
          selectAction: index === 0 ? getViewDetailsDropdown(team) : null,
        }));
      }),
    [myTeams],
  );

  const handleSort = useCallback(
    (columnKey: string, direction: "asc" | "desc") => {
      const apiColumnMap: { [key: string]: string } = {
        'id': 'TEAMID',
        'ubtName': 'TEAMNAME',
      };
      const apiSortColumn = apiColumnMap[columnKey] ?? columnKey.toUpperCase();
      setUiSortColumn(columnKey);
      setSortColumn(apiSortColumn);
      setSortDirection(direction);
      // Reset to page 1 when sorting changes
      dispatch(setCurrentPage(1));
    },
    [dispatch],
  );

  const handleReportSelect = useCallback(
    (selectedReport: DropdownOption) => {
      const nuid = profile?.nuid ? String(profile.nuid) : "";
      if (!nuid) return;

      const teamIds = (myTeams ?? []).map((team) => team?.id as string | number).filter(isDownloadableTeamId);
      if (teamIds.length === 0) return;

      dispatchMyTeamsReport(selectedReport, { nuid, sortColumn, sortDirection, dispatch });
    },
    [dispatch, myTeams, profile, sortColumn, sortDirection],
  );

  const handleDeleteClick = useCallback((selectedRowOrId: unknown) => {
    const teamObj: Record<string, unknown> | null =
      selectedRowOrId && typeof selectedRowOrId === "object"
        ? (selectedRowOrId as Record<string, unknown>)
        : (() => {
            const id = Number(selectedRowOrId);
            if (Number.isNaN(id)) return null;
            return (myTeams?.find((t) => String(t.id) === String(id)) as Record<string, unknown>) ?? null;
          })();
    const teamId = Number(teamObj?.id ?? selectedRowOrId);
    if (Number.isNaN(teamId) && teamId !== 0) return;

    setCurrentTeamId(teamId);
    setResponseForms([
      {
        id: String(teamId),
        title: (teamObj?.ubtName as string) ?? "",
        subject: "",
        message: "",
      },
    ]);
    setShowDeleteModal(true);
  }, [myTeams]);

  const handleCancelDelete = useCallback(() => {
    setShowDeleteModal(false);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (!currentTeamId) return;

    // Close modal immediately so UI does not block while async delete runs
    setShowDeleteModal(false);

    try {
      await dispatch(deleteMyTeam(currentTeamId)).unwrap();

      // Save the current response name before deleting
      const deletedName = responseForms[0]?.title || "";
      // Reset to first page and re-fetch list and count so pagination resets to page 1
      dispatch(setCurrentPage(1));
      // Immediately fetch the first page to ensure UI shows page 1 with full page size
      const refetchPayload: FetchMyTeamsPayload = {
        page: 1,
        perPage,
        sortColumn: sortColumn ?? "TEAMID",
        sortOrder: sortDirection.toUpperCase() as "ASC" | "DESC",
        countOnly: false,
      };
      dispatch(fetchMyTeamsUBTList(refetchPayload));
      dispatch(fetchMyTeamsCount());
      // Show delete success banner
      setDeletedResponseName(deletedName);
      setShowDeleteBanner(true);

      // Hide banner after 5 seconds
      setTimeout(() => {
        setShowDeleteBanner(false);
      }, 5000);

      // Reset to empty form
      setResponseForms([{ id: "1", title: "", subject: "", message: "" }]);

    } catch {
      // Modal already closed above; delete failure is silent at this layer
    }
  }, [currentTeamId, dispatch, responseForms]);

  if (profileLoading) return <Loader />;

  const renderContent = () => {
    if (loadingList) {
      return <Loader />;
    }

    if (error) {
      return (
        <div className="flex flex-col items-center justify-center min-h-[400px] px-4">
          <img
            src="/assets/images/alertSolid.svg"
            alt="error-icon"
            className="w-16 h-16 mb-4"
          />
          <h3 className="h3 text-center mb-2">Something Went Wrong</h3>
        </div>
      );
    }

    return (
      <div>
        <CardTable>
          {rows.length > 0 ? (
            <>
              <Table
                columns={columns}
                rows={rows}
                actionKey="ubtName"
                onActionClick={handleActionClick}
                onSort={(columnKey, sortDirection) =>
                  handleSort(columnKey, sortDirection.toLowerCase() as "asc" | "desc")
                }
                sortColumn={uiSortColumn}
                sortDirection={sortDirection.toUpperCase() as "ASC" | "DESC"}
              />
              <div className="mt-4 mobile:relative mobile:left-7.5">
                <Pagination
                  totalItems={totalItems}
                  itemsPerPage={perPage}
                  currentPage={currentPage}
                  onPageChange={handlePageChange}
                  hidePaginationCount={true}
                />
              </div>
            </>
          ) : (
            <div></div>
          )}
        </CardTable>
      </div>
    );
  };

  return (
    <div className="min-h-[70vh] lg:mx-40! mx-5!">
      <BreadCrumbs
        previousPage="UBT Home"
        onPreviousClick={handleBreadcrumbPrevious}
      />
      <div className="flex justify-between items-end mb-4! md:mb-0! desktop:pb-4! ml-4! margin-top-4u desktop:mt-12! md:mt-0! mobile:mt-3.5!">
        <div>
          <h1 className="h1 tablet:mt-8! mobile:mt-3.5!">My Teams</h1>
          <p className="p3 -book mt-2 md:mb-0!">
            Teams with which you are associated
          </p>
          {showTocExportErrorBanner && tocExportError && (
            <div
              role="alert"
              aria-live="assertive"
              className="ds-banner ds-banner__bg--white ds-banner--icon !mb-[24px]"
              style={{ border: '1px solid var(--Color-Red, #C40000)', padding: '1px' }}
            >
              <div className="ds-banner__icon-img">
                <img
                  src="/assets/images/alertSolid.svg"
                  alt=""
                  style={{ width: '38px', height: '38px' }}
                />
              </div>
              <div className="ds-banner__container">
                <div className="ds-banner__main flex flex-col">
                  <div className="ds-banner__main-body p1 -medium" style={{ color: 'var(--Color-Inky, #0D1C3D)', marginTop: '7px' }}>
                    {tocExportError}
                  </div>
                </div>
              </div>
            </div>
          )}
          {showDeleteBanner && (
            <div ref={notificationRef} className="mobile:w-fit! desktop:w-3xl! mt-6!">
              <Notifications
                variant="Success"
                heading={`${deletedResponseName} has been deleted.`}
              />
            </div>
          )}
          <h3 className="mt-6!">{totalCount} {totalCount === 1 ? 'team' : 'teams'} Found <span><PopUp message={helperTextContent}></PopUp></span></h3>
        </div>

        {!loading && (myTeams?.length ?? 0) > 0 && (
          <div className="flex mb-2! relative">
            <Dropdown
              options={getMyTeamsOptions()}
              menuClassName="w-full! min-w-85!"
              buttonClassName="w-full! min-w-50!"
              onSelect={handleReportSelect}
              buttonLabel="Select a Report"
              disabled={reportLoading || pdfDownloadLoading || exportUBTDetailsLoading || tocExportLoading}
            />
            {(reportLoading || pdfDownloadLoading || exportUBTDetailsLoading || tocExportLoading) && (
              <div className="absolute inset-0 flex items-center justify-center bg-white/50 rounded">
                <Loader />
              </div>
            )}
          </div>
        )}

      </div>

      {renderContent()}

      <div className="mb-10!">
        <div className="-mb-27! tablet:hidden">
          <ScrollToTopBtn />
        </div>
      </div>
      <Modal
        isOpen={showDeleteModal}
        title="Remove Team"
        clearBtn="Cancel"
        submitBtn="Remove Team"
        onSecondary={handleCancelDelete}
        onPrimary={handleConfirmDelete}
        onClose={handleCancelDelete}
      >
        <div className="flex items-center gap-3">
          <img
            src="/assets/images/alertSolid.svg"
            alt="alert-icon"
            className="w-10 h-10 shrink-0"
          />
          <p className="p3 -book mb-4!">
            Do you want to remove this team from your My Teams page?
          </p>
        </div>
        <style>{MODAL_STYLES}</style>
      </Modal>
    </div>
  );
};

export default MyTeams;